import { useEffect, useState } from 'react';
import axios from 'axios';
import { Building2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Section reutilisable "Ils nous ont fait confiance"
 * A afficher sur les pages a fort enjeu conversion (Home, Spectacles, niches B2B).
 * Ne s'affiche que si au moins 1 partenaire existe (sinon rien = pas de bruit visuel).
 *
 * Props:
 *  - variant: "compact" (bandeau court) | "full" (grid classique)
 *  - title: titre optionnel
 *  - subtitle: sous-titre optionnel
 */
export default function PartnersSection({
  variant = 'compact',
  title = 'Ils nous ont fait confiance',
  subtitle = 'Mairies, écoles, comités d\'entreprise, associations et festivals en Bretagne',
}) {
  const [partners, setPartners] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/partners`)
      .then((res) => {
        if (!cancelled && Array.isArray(res.data)) setPartners(res.data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!loaded || partners.length === 0) return null;

  const gridClass =
    variant === 'compact'
      ? 'grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4 items-center'
      : 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6';

  return (
    <section className="py-14 bg-white border-y border-ink/5" data-testid="partners-section-shared">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-ink mb-2">
            <Building2 className="w-6 h-6 inline-block mr-2 text-primary" />
            {title}
          </h2>
          <p className="text-sm text-ink-muted max-w-2xl mx-auto">{subtitle}</p>
        </div>
        <div className={gridClass}>
          {partners.map((partner) => (
            <div
              key={partner.id}
              className="bg-white p-3 rounded-xl border border-ink/5 hover:border-primary/30 hover:shadow-md transition-all flex flex-col items-center justify-center min-h-[90px] grayscale hover:grayscale-0"
              data-testid={`partner-shared-${partner.id}`}
              title={partner.name}
            >
              {partner.logo ? (
                <img
                  src={partner.logo}
                  alt={partner.name}
                  loading="lazy"
                  className="max-h-12 max-w-full object-contain"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-primary/50" />
                </div>
              )}
              <p className="text-[11px] font-medium text-center text-ink-muted mt-1.5 line-clamp-1">
                {partner.name}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
